<template>
  <div class="mc-docs">
    <label
      v-for="item in items"
      :key="item.key"
      class="mc-doc"
      :class="{ 'is-on': !!captured[item.key] }"
    >
      <span v-if="item.required && !captured[item.key]" class="mc-rq"
        >REQUIRED</span
      >
      <input
        type="file"
        :accept="item.accept || 'image/*'"
        :capture="item.accept === 'video/*' ? undefined : 'environment'"
        class="mc-doc-input"
        @change="onChange(item, $event)"
        @click="onClick(item, $event)"
      />
      <div class="mc-doc-icon">
        <svg
          v-if="captured[item.key]"
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <svg
          v-else
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M4 8a2 2 0 012-2h1.2l.8-1.4A1.6 1.6 0 019.4 3.8h5.2c.6 0 1.1.3 1.4.8L17 6h1a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V8z"
          />
          <circle cx="12" cy="12.5" r="3.2" />
        </svg>
      </div>
      <b>{{ item.label }}</b>
      <span v-if="item.hint" class="mc-doc-hint">{{ item.hint }}</span>
      <span class="mc-doc-status">{{
        captured[item.key]
          ? captured[item.key].uploading
            ? 'Uploading…'
            : captured[item.key].name || 'Attached'
          : 'Tap to capture'
      }}</span>
    </label>
  </div>
</template>

<script>
// Shared by the online-form review step today; the paper-upload and AI
// journeys capture photos/pages the same way and can reuse this as-is.
const MAX_SIZE_BYTES = 8 * 1024 * 1024

export default {
  name: 'PhotoDocCapture',
  props: {
    // [{ key, label, hint?, required?, accept? }]
    items: { type: Array, required: true },
    // { [key]: { name } | true }
    captured: { type: Object, default: () => ({}) },
  },
  methods: {
    onClick(item, event) {
      // tapping an already-attached tile removes it instead of reopening the
      // file picker, matching the design's capture()/confirm() behaviour.
      // Blocked mid-upload to avoid a race with the in-flight request.
      if (this.captured[item.key] && !this.captured[item.key].uploading) {
        event.preventDefault()
        if (window.confirm('Remove this attachment?'))
          this.$emit('remove', item.key)
      } else if (this.captured[item.key]) {
        event.preventDefault()
      }
    },
    onChange(item, event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (file.size > MAX_SIZE_BYTES) {
        this.$emit(
          'error',
          item.key,
          'File is larger than 8MB — please choose a smaller one.'
        )
        return
      }
      this.$emit('select', item.key, file)
    },
  },
}
</script>

<style scoped>
.mc-docs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.mc-doc {
  display: block;
  border: 1.5px dashed #d9dbe9;
  border-radius: 14px;
  background: #fff;
  padding: 14px 12px;
  text-align: center;
  cursor: pointer;
  transition: 0.15s;
  position: relative;
  overflow: hidden;
}

.mc-doc.is-on {
  border-style: solid;
  border-color: #27ae60;
  /* solid colour (pre-blended over white) instead of an rgba tint — a flat
     contrast check can't resolve alpha-over-unknown-parent and misreads the
     raw translucent value as darker than what actually renders */
  background: #f2faf5;
}

.mc-doc-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
}

.mc-doc-icon {
  width: 34px;
  height: 34px;
  margin: 0 auto 8px;
  border-radius: 10px;
  background: #eff0f6;
  display: grid;
  place-items: center;
  color: #4e4b66;
}

.mc-doc.is-on .mc-doc-icon {
  background: #dcf2e6;
  color: #166534;
}

.mc-doc b {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #333333;
  line-height: 1.3;
}

.mc-doc-hint {
  display: block;
  font-size: 11px;
  color: #6e7191;
  margin-top: 2px;
}

.mc-doc-status {
  display: block;
  font-size: 11px;
  color: #6e7191;
  margin-top: 3px;
}

.mc-doc.is-on .mc-doc-status {
  color: #166534;
  font-weight: 600;
}

.mc-rq {
  position: absolute;
  top: 7px;
  right: 8px;
  font-size: 9px;
  font-weight: 700;
  color: #eb5757;
  letter-spacing: 0.3px;
}
</style>
